import React, { useEffect, useRef, useState } from 'react';
import { useWallet } from '../../context/WalletContext';
import { useContracts } from '../../context/ContractContext';
import Card from '../common/Card';
import InlineLoader from '../common/InlineLoader';
import { formatUSDT, formatDateShort, invTypeLabel, pct } from '../../utils/format';

export default function InvestmentHistory() {
  const { account } = useWallet();
  const { main } = useContracts();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    if (!account) { setLoading(false); return; }
    const load = async () => {
      try {
        const uid = await main.addrToId(account);
        if (uid.eq(0)) { if (mountedRef.current) setLoading(false); return; }
        const count = await main.getUserInvCount(uid);
        const n = Number(count.toString());
        const out = [];
        for (let i = 0; i < n; i++) {
          const [at, roiInfo] = await Promise.all([
            main.getUserInvAt(uid, i),
            main.getUserInvROI(uid, i)
          ]);
          out.push({
            i,
            amount: at[0],
            timestamp: at[1],
            roiEarned: at[2],
            levelEarned: at[3],
            boosterEarned: at[4],
            active: at[5],
            lockedROIRate: roiInfo[0],
            lockedMaxDays: roiInfo[1],
            roiPaid: roiInfo[2],
            invType: Number(roiInfo[3])
          });
        }
        if (!mountedRef.current) return;
        setItems(out.reverse());
        setLoading(false);
      } catch (_) {
        if (mountedRef.current) setLoading(false);
      }
    };
    setLoading(true);
    load();
    return () => { mountedRef.current = false; };
  }, [account, main]);

  if (loading) return <Card title="Investment History"><InlineLoader /></Card>;
  if (!account) return <Card title="Investment History"><div className="empty-state">Connect wallet</div></Card>;
  if (items.length === 0) return <Card title="Investment History"><div className="empty-state">No investments yet</div></Card>;

  return (
    <Card title={`Investment History (${items.length})`}>
      <div className="table-wrap table-scroll">
        <table className="table">
          <thead>
            <tr>
              <th>#</th>
              <th>Date</th>
              <th>Type</th>
              <th className="text-right">Amount</th>
              <th className="text-right">ROI Rate</th>
              <th className="text-right">Days Paid</th>
              <th className="text-right">ROI Earned</th>
              <th className="text-right">Level Earned</th>
              <th className="text-center">Status</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.i}>
                <td>#{items.length - items.indexOf(it)}</td>
                <td>{formatDateShort(it.timestamp)}</td>
                <td>
                  <span className={`badge ${it.invType === 0 ? 'badge-primary' : it.invType === 1 ? 'badge-warning' : 'badge-success'}`}>
                    {invTypeLabel(it.invType)}
                  </span>
                </td>
                <td className="text-right text-primary">${formatUSDT(it.amount)}</td>
                <td className="text-right">{pct(it.lockedROIRate)}</td>
                <td className="text-right">{it.roiPaid.toString()}/{it.lockedMaxDays.toString()}</td>
                <td className="text-right text-success">${formatUSDT(it.roiEarned)}</td>
                <td className="text-right text-primary">${formatUSDT(it.levelEarned)}</td>
                <td className="text-center">
                  <span className={`badge ${it.active ? 'badge-success' : 'badge-muted'}`}>
                    {it.active ? 'ACTIVE' : 'CLOSED'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
